import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API = "/api";
const AuthContext = createContext(null);
const USER_KEY = "logsite_user";

// The last confirmed user, used only when the server can't be reached, so loggers keep working offline.
const cachedUser = () => { try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; } };
const cacheUser = u => { try { u ? localStorage.setItem(USER_KEY, JSON.stringify(u)) : localStorage.removeItem(USER_KEY); } catch {} };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then(async r => {
        if (r.ok) {
          const data = await r.json();
          if (data?.user) { setUser(data.user); cacheUser(data.user); }
        } else if (r.status === 401) {
          cacheUser(null);
        } else {
          setUser(cachedUser());
        }
      })
      .catch(() => setUser(cachedUser()))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (identifier, code) => {
    const res = await fetch(`${API}/auth/verify-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ identifier, code }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Sisselogimine ebaõnnestus");
    setUser(data.user);
    cacheUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    cacheUser(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
